import { inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

/** ข้อมูล session ของกรรมการภายนอก (ผูกกับโครงการเดียว) */
export interface CommitteeSession {
  /** opaque session token สำหรับแนบ Authorization */
  access_token: string;
  /** เวลาหมดอายุ (ISO) */
  expires_at: string;
  /** โครงการที่ถูกเชิญ */
  project_id: string;
  project_title: string;
  display_name?: string | null;
}

/**
 * จัดการ session token ของกรรมการภายนอก (แยกจาก owner/admin)
 * เก็บใน localStorage — token เป็น opaque ผูกกับโครงการเดียว
 */
@Injectable({ providedIn: 'root' })
export class CommitteeTokenService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly KEY = 'committee_session';

  /** session ปัจจุบัน (reactive) */
  readonly session = signal<CommitteeSession | null>(this.read());

  private isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  /** อ่าน session จาก localStorage */
  private read(): CommitteeSession | null {
    if (typeof localStorage === 'undefined') return null;
    try {
      const raw = localStorage.getItem(this.KEY);
      return raw ? JSON.parse(raw) as CommitteeSession : null;
    } catch { return null; }
  }

  /** บันทึก session หลัง verify สำเร็จ */
  setSession(s: CommitteeSession): void {
    this.session.set(s);
    if (this.isBrowser()) localStorage.setItem(this.KEY, JSON.stringify(s));
  }

  /** token สำหรับแนบ header (null ถ้าหมดอายุ/ไม่มี) */
  getAccessToken(): string | null {
    const s = this.session();
    if (!s || this.isExpired()) return null;
    return s.access_token;
  }

  /** session หมดอายุหรือยัง */
  isExpired(): boolean {
    const s = this.session();
    if (!s?.expires_at) return true;
    return Date.parse(s.expires_at) <= Date.now();
  }

  /** มี session ใช้งานได้ไหม */
  isValid(): boolean {
    return !!this.session() && !this.isExpired();
  }

  /** ล้าง session (logout / หมดอายุ) */
  clear(): void {
    this.session.set(null);
    if (this.isBrowser()) localStorage.removeItem(this.KEY);
  }
}
